import { Link } from "react-router-dom";
import { prettyType } from "@/lib/intel";
import { Dna, Route, Users, AlertTriangle, Target, GitCompareArrows } from "lucide-react";

const STAGE_TONE = {
  evidenced: "bg-teal-500 text-white border-teal-500",
  partial: "bg-amber-400 text-white border-amber-400",
  gap: "bg-slate-200 text-slate-500 border-slate-300",
};

function Field({ label, children }) {
  return (
    <div className="border-b border-slate-100 py-1.5">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{children}</div>
    </div>
  );
}

export function NetworkBrief({ profile, similar = [] }) {
  if (!profile) return <div className="text-sm text-slate-500">Not analyzed yet.</div>;
  const b = profile.behaviour || {};
  const dna = profile.dna_signature || {};

  return (
    <div className="space-y-6" data-testid="network-brief">
      {profile.summary && (
        <p className="text-sm text-slate-700 leading-relaxed border-l-2 border-blue-500 pl-3">{profile.summary}</p>
      )}

      <section>
        <h3 className="font-display font-bold text-sm tracking-tight mb-2 flex items-center gap-1.5"><Route className="h-4 w-4 text-blue-600" /> Operational sequence</h3>
        <div className="flex flex-wrap gap-1.5" data-testid="workflow-stages">
          {(profile.workflow_stages || []).map((s) => (
            <div key={s.stage} data-testid={`stage-${s.status}`} title={`${s.detail}${s.intervention ? ` · Intervene: ${s.intervention}` : ""}`}
              className={`text-[11px] font-mono px-2 py-1 rounded-md border ${STAGE_TONE[s.status] || STAGE_TONE.gap}`}>
              {s.stage}
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-2 text-[10px] text-slate-400 font-mono">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-teal-500" /> evidenced</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> partial</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300" /> gap</span>
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-x-6">
        <div>
          <h3 className="font-display font-bold text-sm tracking-tight mb-1">Behaviour</h3>
          <Field label="Recruitment channel">{b.recruitment_channel || "unknown"}</Field>
          <Field label="Transport methods">{(b.transport_methods || []).join(", ") || "unknown"}</Field>
          <Field label="Movement pattern">{b.movement_pattern || "unknown"}</Field>
          <Field label="Operational timing">{b.operational_timing || "unknown"}</Field>
          <Field label="Exploitation type">{b.exploitation_type || "unknown"}</Field>
        </div>
        <div>
          <h3 className="font-display font-bold text-sm tracking-tight mb-1 flex items-center gap-1.5"><Dna className="h-4 w-4 text-purple-600" /> Network DNA</h3>
          {Object.entries(dna).map(([k, v]) => (
            <Field key={k} label={k}>{v}</Field>
          ))}
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-6">
        <div>
          <h3 className="font-display font-bold text-sm tracking-tight mb-2 flex items-center gap-1.5"><Users className="h-4 w-4 text-blue-600" /> Roles present ({(profile.roles || []).length})</h3>
          <ul className="space-y-1.5" data-testid="roles-present">
            {(profile.roles || []).map((r, i) => (
              <li key={i} className="text-sm flex items-start justify-between gap-2">
                <span><span className="font-medium">{prettyType(r.role)}</span> <span className="text-slate-500">— {r.entity_label.replace("[DEMO] ", "")}</span></span>
                <span className="text-[11px] font-mono text-slate-400 shrink-0">{Math.round((r.confidence || 0) * 100)}%</span>
              </li>
            ))}
            {(profile.roles || []).length === 0 && <li className="text-xs text-slate-400">No roles classified.</li>}
          </ul>
        </div>
        <div className="space-y-4">
          <div>
            <h3 className="font-display font-bold text-sm tracking-tight mb-2 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-600" /> Likely missing roles</h3>
            <div className="flex flex-wrap gap-1.5" data-testid="missing-roles">
              {(profile.missing_roles || []).map((r, i) => (
                <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800">{prettyType(r)}</span>
              ))}
              {(profile.missing_roles || []).length === 0 && <span className="text-xs text-slate-400">None flagged.</span>}
            </div>
          </div>
          <div>
            <h3 className="font-display font-bold text-sm tracking-tight mb-2 flex items-center gap-1.5"><Target className="h-4 w-4 text-red-600" /> Intervention points</h3>
            <ul className="space-y-1 list-disc pl-4">
              {(profile.intervention_points || []).map((p, i) => <li key={i} className="text-sm text-slate-700">{p}</li>)}
              {(profile.intervention_points || []).length === 0 && <li className="text-xs text-slate-400 list-none">None suggested.</li>}
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-display font-bold text-sm tracking-tight mb-2 flex items-center gap-1.5"><GitCompareArrows className="h-4 w-4 text-indigo-600" /> Similar-network leads</h3>
        <p className="text-[11px] text-slate-400 mb-2">Similarity is an investigative lead only — not evidence that networks are the same.</p>
        <div className="space-y-2" data-testid="similar-networks">
          {similar.filter((m) => m.similarity > 0).map((m, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 text-sm flex items-center justify-between gap-3">
              <div>
                <span className="font-medium">{m.network}</span>
                <div className="text-xs text-slate-500">shared: {m.shared_dimensions.map((s) => s.dimension).join(", ") || "—"}</div>
              </div>
              <span className="text-xs font-mono px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700">{Math.round(m.similarity * 100)}% match</span>
            </div>
          ))}
          {similar.filter((m) => m.similarity > 0).length === 0 && <div className="text-xs text-slate-400">No similar analyzed networks found.</div>}
        </div>
      </section>
    </div>
  );
}
